/**
 * SGFAM rows keyed by logical SG short-name (`EWS`, `KMB`, `MK60`, …).
 */
export function indexSgfam(rows) {
    const out = new Map();
    for (const row of rows)
        out.set(row.sgName, row);
    return out;
}
export function indexZst(file) {
    const bySaCode = new Map();
    const byFsw = new Map();
    for (const rec of file.records) {
        if (rec.saCode) {
            const list = bySaCode.get(rec.saCode);
            if (list)
                list.push(rec);
            else
                bySaCode.set(rec.saCode, [rec]);
        }
        if (rec.fsw) {
            const list = byFsw.get(rec.fsw);
            if (list)
                list.push(rec);
            else
                byFsw.set(rec.fsw, [rec]);
        }
    }
    return { file, bySaCode, byFsw };
}
/**
 * AT records by FA code (e.g. `502`).
 */
export function indexAt(records) {
    const out = new Map();
    for (const rec of records)
        out.set(rec.code, rec);
    return out;
}
/**
 * SGFAM rows whose `fa`/`zcs` flag is set. Use this to discover which SG on the chassis
 * carries the vehicle-identity payload (FA-master) or ZCS payload (ZCS-master) without
 * hardcoding a per-chassis table — NCSEXPER itself reads the same flags out of SGFAM.
 *
 * Most chassis have exactly one FA-master, but pre-FA chassis (E36/E38/E39/E46/E53) have
 * several ZCS-masters working together. Callers should be prepared for `[]` (warn — no
 * row carries the flag) or `[row, row, …]`.
 */
export function findSgsByFlag(sgfam, flag) {
    const out = [];
    for (const row of sgfam.values()) {
        if (row[flag] === 1)
            out.push(row);
    }
    return out;
}
/**
 * Index `<BR>ZCSUT.000` by SG. The DatenFile already has every row parsed and
 * tagged with its block (DATEINAME / SG / NO_* / ID_* / MASKE / UMRECHNUNG);
 * this walker just switches on SG rows to bucket the rest. Block layout for
 * the typical pre-FA chassis (E36/E38/E39/E46/E53):
 *
 *   SG               ; row carrying SGNAME — switches the current SG context
 *   NO_CODIERINDEX   ; CDNR="**" wildcard — opens a single all-codings group
 *     - or -
 *   ID_CODIERINDEX   ; CDNR=[7,48,…] explicit coding-index list — opens a
 *                    ;   per-coding-index group; multiple of these per SG
 *   MASKE            ; (GM, SA, VN) valid-template row; belongs to the most
 *                    ;   recent NO_/ID_CODIERINDEX group
 *   UMRECHNUNG       ; (GMALT, SAALT, VNALT, GMNEU, SANEU, VNNEU) conversion
 *                    ;   rule; same grouping as MASKE
 *
 * Hardware-NR / SW-NR / Versions-NR variants are declared in the header but
 * empty for every chassis in NCS-Expert's catalogue, so they're not indexed
 * here. If they ever appear we'd need to extend `ZcsutGroup` with the matching
 * variant slots.
 */
export function indexZcsut(file) {
    const bySg = new Map();
    let currentEcu;
    let currentGroup;
    for (const row of file.rowsInOrder) {
        switch (row.block.name) {
            case 'SG': {
                const sgName = stringValue(row.values.SGNAME);
                if (sgName === undefined)
                    break;
                currentEcu = { sgName, groups: [] };
                currentGroup = undefined;
                bySg.set(sgName, currentEcu);
                break;
            }
            case 'NO_CODIERINDEX': {
                // CDNR scalar with "**" — wildcard group covering any coding index.
                if (!currentEcu)
                    break;
                const group = { codingIndices: null, masks: [], conversions: [] };
                currentEcu.groups.push(group);
                currentGroup = group;
                break;
            }
            case 'ID_CODIERINDEX': {
                // CDNR is a collection of int — coerce to number[].
                if (!currentEcu)
                    break;
                const list = row.values.CDNR;
                const codingIndices = Array.isArray(list)
                    ? list.map((n) => Number(n)).filter((n) => Number.isFinite(n))
                    : [];
                const group = { codingIndices, masks: [], conversions: [] };
                currentEcu.groups.push(group);
                currentGroup = group;
                break;
            }
            case 'MASKE': {
                if (!currentGroup)
                    break;
                const mask = readMask(row.values);
                if (mask)
                    currentGroup.masks.push(mask);
                break;
            }
            case 'UMRECHNUNG': {
                if (!currentGroup)
                    break;
                const conv = readConversion(row.values);
                if (conv)
                    currentGroup.conversions.push(conv);
                break;
            }
            // DATEINAME / BAUREIHE / NO_* HW/SW/VN variants — skip, not used yet.
        }
    }
    return { file, bySg };
}
function stringValue(v) {
    return typeof v === 'string' ? v : undefined;
}
function bytesToHex(v) {
    if (v && typeof v === 'object' && 'bytes' in v) {
        const b = v.bytes;
        let s = '';
        for (const byte of b)
            s += byte.toString(16).padStart(2, '0').toUpperCase();
        return s;
    }
    return undefined;
}
function readMask(values) {
    const gm = stringValue(values.GM);
    const saHex = bytesToHex(values.SA);
    const vnHex = bytesToHex(values.VN);
    if (gm === undefined || saHex === undefined || vnHex === undefined)
        return null;
    return { gm, saHex, vnHex };
}
function readConversion(values) {
    const gmOld = stringValue(values.GMALT);
    const saOldHex = bytesToHex(values.SAALT);
    const vnOldHex = bytesToHex(values.VNALT);
    const gmNew = stringValue(values.GMNEU);
    const saNewHex = bytesToHex(values.SANEU);
    const vnNewHex = bytesToHex(values.VNNEU);
    if (gmOld === undefined ||
        saOldHex === undefined ||
        vnOldHex === undefined ||
        gmNew === undefined ||
        saNewHex === undefined ||
        vnNewHex === undefined) {
        return null;
    }
    return { gmOld, saOldHex, vnOldHex, gmNew, saNewHex, vnNewHex };
}
//# sourceMappingURL=indexes.js.map